import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { request } from '../utils/api-client';
import { AppRoutes } from './routes';
import { getTenantApiPath } from './routing';
import { withTelegramAuth } from './telegramAuth';
import { useTelegramAppTheme } from './hooks/useTelegramAppTheme';
import { StaffRolesProvider, type StaffRolesMap } from './context/StaffRolesContext';

interface ServicesResponse {
  data?: unknown;
  staffRoles?: unknown;
}

function normalizeServices(input: unknown): string[] {
  if (!Array.isArray(input)) {
    return [];
  }

  const normalized = input
    .filter((value): value is string => typeof value === 'string')
    .map((service) => service.trim())
    .filter((service) => service.length > 0);

  return Array.from(new Set(normalized));
}

function normalizeStaffRoles(input: unknown): StaffRolesMap {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return {};
  }

  const rolesByService = input as Record<string, unknown>;
  const normalized: StaffRolesMap = {};

  for (const [service, rawRoleMap] of Object.entries(rolesByService)) {
    if (!rawRoleMap || typeof rawRoleMap !== 'object' || Array.isArray(rawRoleMap)) {
      continue;
    }

    const roleEntries = Object.entries(rawRoleMap as Record<string, unknown>)
      .filter(([, value]) => typeof value === 'boolean')
      .map(([role, value]) => [role, value] as const);

    if (roleEntries.length === 0) {
      continue;
    }

    normalized[service] = Object.fromEntries(roleEntries) as Record<string, boolean>;
  }

  return normalized;
}

export default function App() {
  useTelegramAppTheme();

  const { t } = useTranslation('shell');
  const [activeServices, setActiveServices] = useState<string[]>([]);
  const [staffRoles, setStaffRoles] = useState<StaffRolesMap>({});
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesErrorKey, setServicesErrorKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadServices = async () => {
      setServicesLoading(true);
      setServicesErrorKey(null);

      try {
        const response = await request<ServicesResponse>(
          getTenantApiPath('/services'),
          withTelegramAuth(),
        );

        if (!cancelled) {
          setActiveServices(normalizeServices(response.data));
          setStaffRoles(normalizeStaffRoles(response.staffRoles));
        }
      } catch {
        if (!cancelled) {
          setServicesErrorKey('errors.loadServices');
          setActiveServices([]);
          setStaffRoles({});
        }
      } finally {
        if (!cancelled) {
          setServicesLoading(false);
        }
      }
    };

    void loadServices();

    return () => {
      cancelled = true;
    };
  }, []);

  const servicesError = servicesErrorKey ? t(servicesErrorKey) : null;

  return (
    <StaffRolesProvider roles={staffRoles}>
      <AppRoutes
        activeServices={activeServices}
        staffRoles={staffRoles}
        servicesLoading={servicesLoading}
        servicesError={servicesError}
      />
    </StaffRolesProvider>
  );
}
